import { position, autoUpdate } from '../floating.js';
import { announce } from '../announce.js';
import { normalize, filterOptions, findByPrefix, ActiveOption, onOutsidePointer } from '../listbox.js';

/**
 * <mellow-select>: a custom listbox select with optional search and multiple selection.
 * With [data-pillbox] the selection is shown as removable pills and the search input
 * lives inside the control (Flux "pillbox").
 *
 * Follows the WAI-ARIA combobox / listbox patterns: DOM focus stays on the trigger,
 * search input or listbox, the highlighted option is exposed via aria-activedescendant.
 *
 * `.value` is a string (single) or array of strings (multiple); changes fire `input` and
 * `change` (CustomEvent, value as detail), so `wire:model` works on the element.
 */
export class MellowSelect extends HTMLElement {
    connectedCallback() {
        if (this._ready) return this.sync();
        this._ready = true;

        this.active = new ActiveOption(this, () => this.search ?? this.listbox);

        // Keep the search field's own events away from wire:model on this element.
        this.addEventListener('input', this.onInnerEvent, true);
        this.addEventListener('change', this.onInnerEvent, true);

        if (this.pillbox) {
            this.search?.addEventListener('keydown', this.onKeydown);
            this.querySelector('.mellow-pillbox-control')?.addEventListener('click', (event) => {
                const remove = event.target.closest('[data-remove]');
                if (remove) {
                    this.toggleValue(remove.dataset.remove, false);
                    this.emit();
                    announce((this.dataset.removedLabel ?? ':label').replace(':label', remove.dataset.label ?? ''));
                } else {
                    this.open();
                }
                this.search?.focus();
            });
        } else {
            this.trigger?.addEventListener('click', () => (this.isOpen ? this.close() : this.open()));
            this.trigger?.addEventListener('keydown', this.onTriggerKeydown);
            this.panel?.addEventListener('keydown', this.onKeydown);
        }

        this.panel?.addEventListener('toggle', this.onToggle);

        this.listbox?.addEventListener('click', (event) => {
            const option = event.target.closest('[role="option"]');
            if (option) this.choose(option);
        });
        this.listbox?.addEventListener('pointermove', (event) => {
            const option = event.target.closest('[role="option"]');
            if (option && option !== this.active.current) this.active.set(option, false);
        });
        // Keep focus in the search input when clicking options.
        this.panel?.addEventListener('pointerdown', (event) => event.target.closest('[role="option"]') && event.preventDefault());

        this.querySelector('[data-mellow-select-clear]')?.addEventListener('click', () => {
            this.setSelected([]);
            this.emit();
            this.trigger?.focus();
        });

        this.sync();
    }

    disconnectedCallback() {
        this._outside?.();
    }

    get pillbox() {
        return this.hasAttribute('data-pillbox');
    }

    get trigger() {
        return this.pillbox ? this.search : this.querySelector('.mellow-listbox-trigger');
    }

    get anchor() {
        return this.pillbox ? this.querySelector('.mellow-pillbox-control') : this.trigger;
    }

    get panel() {
        return this.querySelector(':scope > [popover]');
    }

    get listbox() {
        return this.querySelector('[role="listbox"]');
    }

    get search() {
        return this.querySelector('[data-mellow-select-search]');
    }

    /** Real options – without the "Create …" row. */
    get options() {
        return [...this.querySelectorAll('[role="option"]:not([data-create])')];
    }

    get createOption() {
        return this.querySelector('[data-create]');
    }

    get multiple() {
        return this.hasAttribute('multiple');
    }

    get isOpen() {
        return this.panel?.matches(':popover-open') ?? false;
    }

    get selectedOptions() {
        return this.options.filter((o) => o.getAttribute('aria-selected') === 'true');
    }

    get value() {
        const values = this.selectedOptions.map((o) => o.dataset.value);
        return this.multiple ? values : (values[0] ?? '');
    }

    set value(value) {
        const values = Array.isArray(value) ? value : value === null || value === undefined || value === '' ? [] : [value];
        this.setSelected(values.map(String));
    }

    /** Re-apply derived state, e.g. after Livewire morphed the options. */
    sync() {
        this.render();

        // A morph while searching resets hidden options and the highlight: restore them.
        if (this.isOpen) {
            if (this.search?.value) this.filter(this.search.value, false);
            this.active.refresh();
            if (!this.active.current) this.active.first();
        }
    }

    setSelected(values) {
        const wanted = this.multiple ? values : values.slice(0, 1);
        this.options.forEach((option) => option.setAttribute('aria-selected', String(wanted.includes(option.dataset.value))));
        this.render();
    }

    toggleValue(value, selected) {
        this.options.find((o) => o.dataset.value === value)?.setAttribute('aria-selected', String(selected));
        this.render();
    }

    labelOf(option) {
        return option.querySelector('.mellow-listbox-label')?.textContent.trim() ?? option.textContent.trim();
    }

    render() {
        const labels = this.selectedOptions.map((o) => this.labelOf(o));

        // Rendering is idempotent: DOM changes trigger a re-sync (MutationObserver), so only
        // touch the DOM when something actually changed.
        const display = this.querySelector('.mellow-listbox-value');
        // Options may define a shorter trigger text (data-display) and an icon (data-select-icon).
        const single = !this.multiple ? this.selectedOptions[0] : null;
        if (single?.dataset.display) labels[0] = single.dataset.display;

        const iconSlot = this.querySelector('.mellow-listbox-icon');
        if (iconSlot) {
            const icon = single?.querySelector('[data-select-icon]');
            const current = iconSlot.firstElementChild;
            if (!icon) {
                if (current) iconSlot.replaceChildren();
            } else if (current?.dataset.code !== icon.dataset.code || !current) {
                iconSlot.replaceChildren(icon.cloneNode(true));
            }
        }

        if (display) {
            const text = labels.length === 0
                ? this.dataset.placeholder
                : labels.length <= 2
                    ? labels.join(', ')
                    : (this.dataset.countLabel ?? ':count').replace(':count', labels.length);
            if (display.textContent !== text) display.textContent = text;
            display.toggleAttribute('data-empty', labels.length === 0);
        }

        if (this.pillbox) this.renderPills();

        const clear = this.querySelector('[data-mellow-select-clear]');
        if (clear) clear.hidden = labels.length === 0;

        this.syncHiddenInputs();
    }

    renderPills() {
        const container = this.querySelector('.mellow-pillbox-pills');
        const template = this.querySelector('template[data-pill]');
        if (!container || !template) return;

        const values = this.selectedOptions.map((o) => o.dataset.value);
        const current = [...container.querySelectorAll('[data-remove]')].map((button) => button.dataset.remove);
        if (values.join('\u0000') !== current.join('\u0000')) container.replaceChildren(...this.selectedOptions.map((option) => {
            const label = this.labelOf(option);
            const pill = template.content.firstElementChild.cloneNode(true);
            pill.querySelector('[data-label]').textContent = label;
            const remove = pill.querySelector('[data-remove]');
            remove.dataset.remove = option.dataset.value;
            remove.dataset.label = label;
            remove.setAttribute('aria-label', (this.dataset.removeLabel ?? ':label').replace(':label', label));
            return pill;
        }));

        const placeholder = this.selectedOptions.length ? '' : (this.dataset.placeholder ?? '');
        if (this.search && this.search.placeholder !== placeholder) this.search.placeholder = placeholder;
    }

    /** Plain HTML forms: keep hidden inputs in sync with the selection. */
    syncHiddenInputs() {
        const existing = [...this.querySelectorAll('[data-mellow-select-value]')];
        if (!existing.length) return;

        const name = existing[0].name;
        const values = this.multiple ? this.value : [this.value];
        const wanted = values.length ? values : [''];
        if (wanted.join('\u0000') === existing.map((input) => input.value).join('\u0000')) return;
        existing.forEach((input) => input.remove());

        wanted.forEach((value) => {
            const input = document.createElement('input');
            Object.assign(input, { type: 'hidden', name, value });
            input.setAttribute('data-mellow-select-value', '');
            this.append(input);
        });
    }

    open() {
        if (this.isOpen || this.trigger?.disabled) return;
        this.panel.showPopover();

        // Set up synchronously (not in the async toggle event), so keys typed right after
        // opening already reach the search field.
        this.trigger?.setAttribute('aria-expanded', 'true');
        const anchor = this.anchor;
        this.panel.style.minWidth = `${anchor.offsetWidth}px`;
        this._cleanup?.();
        this._cleanup = autoUpdate(anchor, this.panel, () => position(anchor, this.panel, { placement: 'bottom', align: 'start', offset: 4 }));
        this._outside?.();
        this._outside = onOutsidePointer(this, () => this.close(false));

        this.filter(this.pillbox ? this.search.value : '', false);
        const selected = this.selectedOptions.find((o) => !o.hidden);
        this.active.set(this.pillbox ? this.active.options[0] : (selected ?? this.active.options[0]));
        (this.search ?? this.listbox).focus({ preventScroll: true });
    }

    close(focusTrigger = true) {
        if (!this.isOpen) return;
        this.panel.hidePopover();
        if (focusTrigger) this.trigger?.focus();
    }

    onToggle = (event) => {
        if (event.newState === 'open') return;

        this.trigger?.setAttribute('aria-expanded', 'false');
        this._cleanup?.();
        this._outside?.();
        this._outside = null;
        if (this.search && !this.pillbox) this.search.value = '';
        this.active.set(null);
        if (this.panel.contains(document.activeElement)) this.trigger?.focus();
    };

    onInnerEvent = (event) => {
        if (event.target === this) return;
        event.stopPropagation();

        if (event.type === 'input' && event.target === this.search) {
            if (!this.isOpen) this.open();
            this.filter(this.search.value);
            // Prefer options that start with the query ("2:30" → "2:30 PM" before "12:30 AM").
            const options = this.active.options.filter((o) => !o.hasAttribute('data-create'));
            this.active.set(findByPrefix(options, this.search.value) ?? options[0] ?? this.createOption);
        }
    };

    filter(query, announceResults = true) {
        filterOptions(this.listbox, query);
        this.updateCreate(query);
        const visible = this.options.filter((option) => !option.hidden).length;

        const empty = this.querySelector('.mellow-listbox-empty');
        if (empty) empty.hidden = visible > 0 || Boolean(this.createOption && !this.createOption.hidden);

        // Announce the result count once typing pauses, not on every keystroke.
        clearTimeout(this._announceTimer);
        if (announceResults && normalize(query)) {
            const label = visible === 1 ? this.dataset.resultOne : this.dataset.resultMany;
            this._announceTimer = setTimeout(() => announce(visible ? (label ?? ':count').replace(':count', visible) : empty?.textContent ?? ''), 600);
        }
    }

    /** Show "Create “query”" when the query doesn't match an existing option exactly. */
    updateCreate(query) {
        const row = this.createOption;
        if (!row) return false;

        const text = query.trim();
        const exists = this.options.some((o) => normalize(this.labelOf(o)) === normalize(text));
        row.hidden = !text || exists;
        const label = row.querySelector('[data-query]');
        if (label && label.textContent !== text) label.textContent = text;

        return !row.hidden;
    }

    /** Add a new, user-entered value (or select the existing option with that label). */
    create(text) {
        text = text.trim();
        if (!text) return;

        let option = this.options.find((o) => normalize(this.labelOf(o)) === normalize(text));
        if (!option) {
            option = this.querySelector('template[data-option]').content.firstElementChild.cloneNode(true);
            option.id = `${this.createOption.id}-${Date.now()}`;
            option.dataset.value = text;
            option.querySelector('.mellow-listbox-label').textContent = text;
            this.createOption.before(option);
            announce((this.dataset.createdLabel ?? ':value').replace(':value', text));
        }

        if (!this.multiple) this.options.forEach((o) => o.setAttribute('aria-selected', 'false'));
        option.setAttribute('aria-selected', 'true');
        this.render();

        if (this.search) {
            this.search.value = '';
            this.filter('', false);
        }
        this.active.set(option, false);
        this.emit();
        if (!this.multiple) this.close();
    }

    choose(option) {
        if (!option || option.getAttribute('aria-disabled') === 'true') return;
        if (option.hasAttribute('data-create')) return this.create(this.search?.value ?? '');

        if (this.multiple) {
            this.toggleValue(option.dataset.value, option.getAttribute('aria-selected') !== 'true');
            if (this.pillbox && this.search.value) {
                // Start a fresh search after picking a match.
                this.search.value = '';
                this.filter('', false);
            }
            this.active.set(option, false);
        } else {
            this.setSelected([option.dataset.value]);
            this.close();
        }

        this.emit();
    }

    emit() {
        const value = this.value;
        this.dispatchEvent(new CustomEvent('input', { bubbles: true, detail: value }));
        this.dispatchEvent(new CustomEvent('change', { bubbles: true, detail: value }));
    }

    removeLast() {
        const last = this.selectedOptions.at(-1);
        if (!last) return;
        this.toggleValue(last.dataset.value, false);
        this.emit();
        announce((this.dataset.removedLabel ?? ':label').replace(':label', this.labelOf(last)));
    }

    onTriggerKeydown = (event) => {
        if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(event.key)) {
            event.preventDefault();
            this.open();
            if (event.key === 'ArrowUp') this.active.last();
            return;
        }

        // Typeahead while closed selects the next matching option (like a native select).
        if (!this.multiple && event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
            const match = this.typeahead(event.key, this.selectedOptions[0]);
            if (match) {
                this.setSelected([match.dataset.value]);
                this.emit();
            }
        }
    };

    typeahead(key, after) {
        clearTimeout(this._typeTimer);
        this._typed = (this._typed ?? '') + key;
        this._typeTimer = setTimeout(() => (this._typed = ''), 500);

        const options = this.active.options;
        const index = options.indexOf(after);
        return findByPrefix([...options.slice(index + 1), ...options.slice(0, index + 1)], this._typed)
            ?? findByPrefix(options, this._typed);
    }

    onKeydown = (event) => {
        const inSearch = event.target === this.search;

        // Pillbox: the search input is also the trigger.
        if (!this.isOpen) {
            if (['ArrowDown', 'ArrowUp'].includes(event.key)) {
                event.preventDefault();
                this.open();
                if (event.key === 'ArrowUp') this.active.last();
            } else if (event.key === 'Backspace' && this.pillbox && !this.search.value) {
                this.removeLast();
            }
            return;
        }

        switch (event.key) {
            case 'ArrowDown':
                event.preventDefault();
                return this.active.move(1);
            case 'ArrowUp':
                event.preventDefault();
                return this.active.move(-1);
            case 'Home':
            case 'End':
                if (inSearch) return; // keep text editing keys in the search field
                event.preventDefault();
                return event.key === 'Home' ? this.active.first() : this.active.last();
            case 'PageDown':
            case 'PageUp':
                event.preventDefault();
                return this.active.move(event.key === 'PageDown' ? 10 : -10);
            case 'Enter':
                event.preventDefault();
                if (!this.active.current && this.hasAttribute('data-creatable')) return this.create(this.search?.value ?? '');
                return this.choose(this.active.current);
            case ',':
                // Comma adds the typed value – handy for tags.
                if (inSearch && this.hasAttribute('data-creatable') && this.search.value.trim()) {
                    event.preventDefault();
                    return this.create(this.search.value);
                }
                return;
            case ' ':
                if (inSearch) return;
                event.preventDefault();
                return this.choose(this.active.current);
            case 'Escape':
                event.preventDefault();
                return this.close();
            case 'Tab':
                return this.close(false);
            case 'Backspace':
                if (this.pillbox && !this.search.value) this.removeLast();
                return;
        }

        // Without a search field, typing jumps to matching options.
        if (!this.search && event.key.length === 1 && !event.ctrlKey && !event.metaKey) {
            const match = this.typeahead(event.key, this.active.current);
            if (match) this.active.set(match);
        }
    };
}
